import { DISCIPLINES } from './contracts'
import type { Discipline, SessionStats } from './contracts'
import { mean } from './scoring'

/**
 * Анализ прошедшей тренировки.
 *
 * Правила простые и проверяемые - никакой «нейроаналитики». Каждое
 * наблюдение опирается на величину, которую мы действительно измерили,
 * и ведёт к действию, которое игрок может выполнить в следующий раз.
 *
 * Чего здесь сознательно нет: утверждений про мозг, про перенос навыка
 * на жизнь, про «когнитивный возраст». Мы этого не измеряем.
 */

export type InsightTone = 'good' | 'neutral' | 'watch'

export interface Insight {
  /** Ключ перевода для заголовка */
  readonly titleKey: string
  /** Ключ перевода для совета */
  readonly adviceKey: string
  readonly tone: InsightTone
  readonly params?: Readonly<Record<string, string | number>>
}

export interface SessionSummary {
  readonly gameId: string
  readonly discipline: Discipline
  readonly stats: SessionStats
  /** Уровень, с которого начали заход */
  readonly startLevel: number
}

/** Точность, выше которой игрок явно не на пределе. */
const TOO_EASY = 0.92
/** Точность, ниже которой задача давит. */
const TOO_HARD = 0.55
/** Насколько время должно отличаться от эталона, чтобы это что-то значило. */
const SLOW_RATIO = 1.45
const FAST_RATIO = 0.7

/**
 * Разбор тренировки. Возвращает не больше трёх наблюдений:
 * список из семи пунктов никто не читает, а приоритет теряется.
 */
export function buildInsights(
  sessions: readonly SessionSummary[],
  referenceByGame: Readonly<Record<string, number>>,
): Insight[] {
  if (sessions.length === 0) return []

  const out: Insight[] = []
  const accuracy = mean(sessions.map((s) => s.stats.pc))
  const climbed = sessions.filter((s) => s.stats.level > s.startLevel)
  const dropped = sessions.filter((s) => s.stats.level < s.startLevel)

  // 1. Скорость против точности - самое полезное наблюдение
  const ratios = sessions
    .map((s) => {
      const ref = referenceByGame[s.gameId]
      return ref && ref > 0 && s.stats.rtMs > 0 ? s.stats.rtMs / ref : null
    })
    .filter((r): r is number => r !== null)

  const pace = ratios.length > 0 ? mean(ratios) : null

  if (pace !== null && accuracy >= TOO_EASY && pace > SLOW_RATIO) {
    out.push({
      titleKey: 'insight.accurateSlow.title',
      adviceKey: 'insight.accurateSlow.advice',
      tone: 'good',
    })
  } else if (pace !== null && accuracy < TOO_HARD && pace < FAST_RATIO) {
    out.push({
      titleKey: 'insight.fastSloppy.title',
      adviceKey: 'insight.fastSloppy.advice',
      tone: 'watch',
    })
  }

  // 2. Движение по уровням
  if (climbed.length >= 2) {
    out.push({
      titleKey: 'insight.climbed.title',
      adviceKey: 'insight.climbed.advice',
      tone: 'good',
      params: { n: climbed.length },
    })
  } else if (dropped.length >= 2) {
    out.push({
      titleKey: 'insight.dropped.title',
      adviceKey: 'insight.dropped.advice',
      tone: 'watch',
    })
  }

  // 3. Перекос между дисциплинами
  const scored = groupAccuracy(sessions)
  if (scored.length >= 2) {
    const worst = scored[0] as { discipline: Discipline; accuracy: number }
    const best = scored[scored.length - 1] as { discipline: Discipline; accuracy: number }
    if (best.accuracy - worst.accuracy >= 0.18) {
      out.push({
        titleKey: 'insight.gap.title',
        adviceKey: 'insight.gap.advice',
        tone: 'neutral',
        params: { d: worst.discipline },
      })
    }
  }

  // 4. Запасное наблюдение, если ничего не выделилось
  if (out.length === 0) {
    out.push({
      titleKey: accuracy >= 0.75 ? 'insight.steady.title' : 'insight.building.title',
      adviceKey: accuracy >= 0.75 ? 'insight.steady.advice' : 'insight.building.advice',
      tone: 'neutral',
    })
  }

  return out.slice(0, 3)
}

/** Средняя точность по дисциплинам, где были заходы. Отсортировано по возрастанию. */
function groupAccuracy(
  sessions: readonly SessionSummary[],
): { discipline: Discipline; accuracy: number }[] {
  return DISCIPLINES.map((discipline) => {
    const values = sessions.filter((s) => s.discipline === discipline).map((s) => s.stats.pc)
    return values.length > 0 ? { discipline, accuracy: mean(values) } : null
  })
    .filter((x): x is { discipline: Discipline; accuracy: number } => x !== null)
    .sort((a, b) => a.accuracy - b.accuracy)
}
